import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ArtistAlias, ArtistAliasCreateRequest } from '@/lib/api-admin/types';
import {
  ApiClientError,
  idempotencyKeyFor,
  TransportError,
  type ApiError as ApiErrorPayload,
  type ApiMeta,
} from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';
import { artistQueryKey } from './useArtistQuery';

interface ErrorEnvelopeShape {
  success: false;
  error: ApiErrorPayload;
  meta?: ApiMeta;
}

const throwIfError = (raw: unknown, status: number, op: string): never => {
  const env = raw as ErrorEnvelopeShape | null | undefined;
  if (env && env.success === false && env.error) {
    throw new ApiClientError(env.error, { status, ...(env.meta ? { meta: env.meta } : {}) });
  }
  throw new TransportError(`Artist alias ${op} failed with status ${status}`, raw);
};

export interface CreateAliasInput {
  artistId: string;
  body: ArtistAliasCreateRequest;
}

export const useCreateArtistAliasMutation = () => {
  const qc = useQueryClient();
  return useMutation<ArtistAlias, Error, CreateAliasInput>({
    mutationFn: async ({ artistId, body }) => {
      const path = '/admin/v1/artists/{artistId}/aliases' as const;
      const concretePath = path.replace('{artistId}', artistId);
      const idempotencyKey = idempotencyKeyFor('POST', concretePath, body);
      const { data, error, response } = await apiClient.raw.POST(path, {
        body,
        params: { path: { artistId }, header: { 'Idempotency-Key': idempotencyKey } },
      });
      if (!data || data.success !== true) throwIfError(error ?? data, response.status, 'create');
      return (data as { data: ArtistAlias }).data;
    },
    onSuccess: (_alias, { artistId }) => {
      void qc.invalidateQueries({ queryKey: artistQueryKey(artistId) });
    },
  });
};

export interface DeleteAliasInput {
  artistId: string;
  aliasId: string;
}

export const useDeleteArtistAliasMutation = () => {
  const qc = useQueryClient();
  return useMutation<void, Error, DeleteAliasInput>({
    mutationFn: async ({ artistId, aliasId }) => {
      const path = '/admin/v1/artists/{artistId}/aliases/{aliasId}' as const;
      const concretePath = path
        .replace('{artistId}', artistId)
        .replace('{aliasId}', aliasId);
      const idempotencyKey = idempotencyKeyFor('DELETE', concretePath, '');
      const { error, response } = await apiClient.raw.DELETE(path, {
        params: {
          path: { artistId, aliasId },
          header: { 'Idempotency-Key': idempotencyKey },
        },
      });
      if (response.status >= 400) throwIfError(error, response.status, 'delete');
    },
    onSuccess: (_void, { artistId }) => {
      void qc.invalidateQueries({ queryKey: artistQueryKey(artistId) });
    },
  });
};
